import { LeaveRequest, LeaveStatus } from '../types/leave';
import { LeaveService } from './leave';
import { LeaveAuditService } from './leaveAuditService';
import { sameIdentity } from './identity';
import { announceLeaveWithdrawn } from './leaveNotifications';
import { loadAllUsers } from './userLoader';

/**
 * Cancelling an already-approved leave.
 *
 * Modelled as a two-step request rather than a direct status flip: the employee asks,
 * the manager decides. An approved absence is something colleagues have planned around
 * and that has already drawn down a balance, so it should not disappear on one person's
 * say-so.
 *
 * The intermediate state rides on the existing `status` column as
 * 'Cancellation Requested' (the GraphQL field is a plain String, so no schema change is
 * needed) and the employee's justification is carried in `decisionReason`. Throughout
 * that window the leave keeps consuming balance — see leaveBalanceService — so nobody
 * frees up days merely by asking.
 */
export class LeaveCancellationService {
  /** Date-only comparison in local time, so "today" means the viewer's today. */
  private static startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private static startOf(dateIso: string): Date | null {
    const [y, m, d] = (dateIso || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  /**
   * The checks both routes share: it must be your own leave, in a state that still has
   * something to undo, and not yet started.
   *
   * The start-date rule is the important one: once the first day has arrived the absence
   * has begun to happen, and unwinding it would silently restore balance for time already
   * taken.
   */
  private static commonGate(
    leave: LeaveRequest,
    viewerEmail?: string
  ): { allowed: boolean; reason?: string } {
    if (!viewerEmail || !sameIdentity(leave.employeeEmail, viewerEmail)) {
      return { allowed: false, reason: 'You can only cancel your own leave.' };
    }

    const status = String(leave.status).toUpperCase();
    if (status === String(LeaveStatus.CANCELLED).toUpperCase()) {
      return { allowed: false, reason: 'This leave is already cancelled.' };
    }
    if (status === String(LeaveStatus.REJECTED).toUpperCase()) {
      return { allowed: false, reason: 'A rejected request has nothing to cancel.' };
    }
    if (status === String(LeaveStatus.CANCELLATION_REQUESTED).toUpperCase()) {
      return { allowed: false, reason: 'A cancellation request is already awaiting your manager.' };
    }

    const start = this.startOf(leave.startDate);
    if (!start) return { allowed: false, reason: 'This request has no valid start date.' };
    if (start <= this.startOfToday()) {
      return {
        allowed: false,
        reason: 'Leave can only be cancelled before its start date — this leave has already started.',
      };
    }
    return { allowed: true };
  }

  /**
   * Whether the employee can withdraw this request outright, with no manager involved.
   *
   * Only while it is still Submitted. Nobody has approved anything yet, so there is no
   * decision to unwind and no reason to ask the manager to approve the removal of a
   * request they never acted on — that is pure queue noise for them and a pointless wait
   * for the employee. Withdrawal goes straight to Cancelled.
   *
   * Once a manager HAS approved, this returns false and canRequestCancellation takes over:
   * an approved absence is a commitment the manager made, and taking it back is their
   * decision to confirm.
   */
  static canWithdraw(
    leave: LeaveRequest,
    viewerEmail?: string
  ): { allowed: boolean; reason?: string } {
    const gate = this.commonGate(leave, viewerEmail);
    if (!gate.allowed) return gate;

    if (String(leave.status).toUpperCase() !== String(LeaveStatus.SUBMITTED).toUpperCase()) {
      return { allowed: false, reason: 'Only a request still awaiting approval can be withdrawn.' };
    }
    return { allowed: true };
  }

  /**
   * Whether the employee must ask their manager to cancel.
   *
   * The approved case. A Submitted request is excluded because canWithdraw handles it
   * directly — routing it here would put a cancellation in the manager's queue for a leave
   * they had not approved.
   */
  static canRequestCancellation(
    leave: LeaveRequest,
    viewerEmail?: string
  ): { allowed: boolean; reason?: string } {
    const gate = this.commonGate(leave, viewerEmail);
    if (!gate.allowed) return gate;

    if (String(leave.status).toUpperCase() === String(LeaveStatus.SUBMITTED).toUpperCase()) {
      return {
        allowed: false,
        reason: 'This request has not been approved yet — withdraw it instead.',
      };
    }
    return { allowed: true };
  }

  /**
   * Employee withdraws a request their manager has not yet acted on. Straight to Cancelled.
   *
   * The balance follows automatically: the engine counts Submitted days as pending and
   * Cancelled days as neither pending nor consumed, so withdrawing releases the hold with
   * no separate restore step.
   */
  /**
   * `requestedBy` answers "is this your leave" and `actorId` answers "who do we record".
   *
   * They are separated because they need different values for the same person. The ownership
   * check below compares requestedBy against the leave's employeeEmail with sameIdentity, so it
   * has to be an email address. But an account whose Cognito email attribute is unset has no
   * email, and the caller was passing `user?.email || ''` for both — so that account wrote an
   * empty actor to the audit trail and its withdrawals showed no "Changed By" at all.
   *
   * actorId falls back to the Cognito sub (resolveActorId), which is never a valid identity for
   * the gate but is always a valid thing to record. Defaults to requestedBy so existing callers
   * are unaffected.
   */
  static async withdraw(
    leave: LeaveRequest,
    reason: string,
    requestedBy: string,
    actorId?: string
  ): Promise<LeaveRequest | null> {
    const check = this.canWithdraw(leave, requestedBy);
    if (!check.allowed) throw new Error(check.reason || 'This request cannot be withdrawn.');

    const recordedBy = actorId || requestedBy;
    const updated = await LeaveService.updateLeave({
      id: leave.id,
      status: LeaveStatus.CANCELLED,
      updatedBy: recordedBy,
      decisionReason: reason.trim() || 'Withdrawn by employee before approval.',
    });
    if (!updated) return null;

    LeaveAuditService.logAction(
      leave.id,
      'Leave Withdrawn',
      recordedBy,
      String(leave.status),
      String(LeaveStatus.CANCELLED),
      reason.trim() || 'Withdrawn by employee before approval.',
      leave.employeeEmail,
      leave.employeeName
    );

    window.dispatchEvent(new Event('leave-requests-updated'));
    window.dispatchEvent(new Event('leave-balance-updated'));
    window.dispatchEvent(new Event('storage'));

    /*
     * Tell the manager and HR the request has left their queue.
     *
     * emailLeaveWithdrawn has existed since the cancellation module shipped and nothing ever
     * called it, so a manager's pending list simply lost a row with no explanation. Wired here
     * rather than at the page, for the reason the decision email is wired into
     * updateLeaveStatus: this is the only place a withdrawal can happen.
     *
     * The employee is not copied — they performed the action and are looking at the result.
     */
    await announceLeaveWithdrawn(updated, { managerEmail: await this.managerOf(leave) });
    return updated;
  }

  /**
   * The applicant's reporting manager, or undefined.
   *
   * The leave row carries no managerEmail, so it comes from the directory — the same cached
   * call the leave screens already make. Best-effort: a directory that cannot be reached must
   * not cost an employee their withdrawal, so the announcement goes out to HR alone.
   */
  private static async managerOf(leave: LeaveRequest): Promise<string | undefined> {
    try {
      const users = await loadAllUsers();
      return users.find(u => sameIdentity(u.email, leave.employeeEmail))?.managerEmail;
    } catch (error) {
      console.warn('[LeaveCancellation] could not resolve the reporting manager:', error);
      return undefined;
    }
  }

  /**
   * Employee asks to cancel. The leave stays booked until the manager decides.
   *
   * requestedBy / actorId are split for the same reason as withdraw above.
   */
  static async requestCancellation(
    leave: LeaveRequest,
    reason: string,
    requestedBy: string,
    actorId?: string
  ): Promise<LeaveRequest | null> {
    const check = this.canRequestCancellation(leave, requestedBy);
    if (!check.allowed) throw new Error(check.reason || 'This leave cannot be cancelled.');
    if (!reason.trim()) throw new Error('A cancellation reason is required.');

    const recordedBy = actorId || requestedBy;
    const updated = await LeaveService.updateLeave({
      id: leave.id,
      status: LeaveStatus.CANCELLATION_REQUESTED,
      updatedBy: recordedBy,
      decisionReason: reason.trim(),
    });
    if (!updated) return null;

    LeaveAuditService.logAction(
      leave.id,
      'Leave Cancellation Requested',
      recordedBy,
      String(leave.status),
      String(LeaveStatus.CANCELLATION_REQUESTED),
      reason.trim(),
      leave.employeeEmail,
      leave.employeeName
    );

    window.dispatchEvent(new Event('leave-requests-updated'));
    window.dispatchEvent(new Event('leave-balance-updated'));
    window.dispatchEvent(new Event('storage'));
    return updated;
  }

  /**
   * Manager's decision.
   *
   * Approving moves the leave to Cancelled, at which point the balance engine stops
   * counting it as consumed — the days come back automatically, with no separate
   * "restore" step that could disagree with the request itself.
   *
   * Rejecting returns it to Approved: the absence stands.
   */
  static async decideCancellation(
    leave: LeaveRequest,
    approve: boolean,
    decidedBy: string,
    note?: string
  ): Promise<LeaveRequest | null> {
    const nextStatus = approve ? LeaveStatus.CANCELLED : LeaveStatus.APPROVED;

    const updated = await LeaveService.updateLeave({
      id: leave.id,
      status: nextStatus,
      updatedBy: decidedBy,
      decisionReason: note?.trim() || (approve ? 'Cancellation approved' : 'Cancellation rejected'),
    });
    if (!updated) return null;

    LeaveAuditService.logAction(
      leave.id,
      approve ? 'Leave Cancellation Approved' : 'Leave Cancellation Rejected',
      decidedBy,
      String(LeaveStatus.CANCELLATION_REQUESTED),
      String(nextStatus),
      note?.trim() || (approve ? 'Leave cancelled; balance restored.' : 'Leave stands.'),
      leave.employeeEmail,
      leave.employeeName
    );

    window.dispatchEvent(new Event('leave-requests-updated'));
    window.dispatchEvent(new Event('leave-balance-updated'));
    window.dispatchEvent(new Event('storage'));
    return updated;
  }

  /** Requests awaiting a cancellation decision, for the approvals queue. */
  static pendingCancellations(leaves: LeaveRequest[]): LeaveRequest[] {
    const target = String(LeaveStatus.CANCELLATION_REQUESTED).toUpperCase();
    return leaves.filter(l => String(l.status).toUpperCase() === target);
  }
}
